import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Calendar, Clock, Video, Building, ArrowRight, Home, FileText, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BRAND } from '../data/mock';
import { getAppointment } from '../services/api';

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('id');
  const token = searchParams.get('token');
  const paymentSuccess = searchParams.get('payment') === 'success';
  
  const [isLoading, setIsLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch booking details
        if (bookingId) {
          const appointmentData = await getAppointment(bookingId, token);
          setBooking(appointmentData);
        }
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError('Failed to load booking details');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [bookingId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDF9F5] to-white py-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#2F5233] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDF9F5] to-white py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link to="/book">
            <Button className="bg-[#2F5233] hover:bg-[#2F5233]/90">
              Try Again
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF9F5] to-white py-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Icon */}
        <div className="text-center mb-10">
          <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-14 h-14 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-[#2F5233]">Booking Submitted!</h1>
          <p className="text-gray-600 mt-2">Your appointment request has been received</p>
        </div>

        {/* Booking Details Card */}
        {booking && (
          <Card className="shadow-xl border-0 mb-8">
            <CardContent className="p-8">
              <div className="flex items-center justify-between pb-6 border-b">
                <div>
                  <p className="text-sm text-gray-500">Booking Reference</p>
                  <p className="text-xl font-bold text-[#2F5233]">{booking.booking_id}</p>
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                  booking.status === 'CONFIRMED' 
                    ? 'bg-emerald-100 text-emerald-800' 
                    : booking.status === 'PENDING'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {booking.status === 'CONFIRMED' ? 'Confirmed' : 
                   booking.status === 'PENDING' ? 'Pending Approval' : 'Requested'}
                </div>
              </div>

              <div className="py-6 space-y-4">
                <div className="flex items-center space-x-4">
                  {booking.consultation_type === 'ONLINE' ? (
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Video className="w-6 h-6 text-blue-600" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Building className="w-6 h-6 text-emerald-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">
                      {booking.consultation_type === 'ONLINE' ? 'Online Consultation' : 'In-Clinic Visit'}
                    </p>
                    <p className="text-sm text-gray-500">with {BRAND.doctorName}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Date</p>
                      <p className="font-medium text-gray-900">{formatDate(booking.appointment_date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Time</p>
                      <p className="font-medium text-gray-900">{formatTime(booking.time_slot)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Payment Status</p>
                    <p className={`font-medium ${
                      booking.payment_status === 'PAID_ONLINE' ? 'text-emerald-600' : 'text-amber-600'
                    }`}>
                      {booking.payment_status === 'PAID_ONLINE' ? 'Paid Online' : 
                       booking.payment_status === 'PAYMENT_PENDING' ? 'Pay at Clinic' : 'Payment Pending'}
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">₹{booking.amount?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start space-x-2">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5"></span>
              <span>The doctor will review your booking request</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5"></span>
              <span>You'll receive a confirmation email and WhatsApp message</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5"></span>
              <span>
                {booking?.consultation_type === 'ONLINE' 
                  ? 'A video call link will be shared before your appointment' 
                  : 'Visit the clinic at the scheduled time'}
              </span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          {booking && (
            <Link to={`/dashboard/${booking.booking_id}?token=${booking.access_token || ''}`} className="flex-1">
              <Button className="w-full bg-[#2F5233] hover:bg-[#2F5233]/90">
                <FileText className="w-4 h-4 mr-2" />
                View Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
          <Link to="/" className="flex-1">
            <Button variant="outline" className="w-full">
              <Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
